/**
 * Turn a Nexi payment terminal failure into an instruction the cashier can act on.
 *
 * Shared by the invoice card payment and the till (Kassamyynti) - both call the same
 * TerminalPaymentService, so both must explain a failure the same way.
 *
 * The single most common failure on a workshop's first day with a terminal is that
 * neither reader app (Pääte or the older Raportit) is in front on the device: Nexi
 * only accepts a payment while one of them is open, and otherwise reports it in
 * `result_description` while `result_code` stays a generic NETWORK_ERROR. Nexi
 * phrases these reader conditions in prose and has shipped more than one wording,
 * so they are matched as substrings rather than keyed on an exact sentence.
 */

// Keep in sync with ROUTINE_TERMINAL_CONDITIONS in invoicing/services/terminal_payment.py -
// that list decides what stays out of Sentry, this one decides what the operator reads.
const CONDITION_MARKERS = [
  { marker: 'not in foreground', key: 'terminal.conditions.appNotInForeground' },
  { marker: 'terminal not connected', key: 'terminal.conditions.terminalNotConnected' },
  { marker: 'no connection to terminal', key: 'terminal.conditions.terminalNotConnected' },
  { marker: 'terminal not available', key: 'terminal.conditions.terminalNotConnected' },
  { marker: 'terminal is busy', key: 'terminal.conditions.terminalBusy' },
];

// HTTP 400/409 errors from Nexi, matched inside the error string.
const KNOWN_ERRORS = ['Too many unconfirmed transactions'];

/**
 * Best translated explanation for a terminal result, or '' when we recognise nothing.
 *
 * `data` is the terminal payload: { result_code, result_description, error } from a
 * failed card capture, or an API error body carrying the same fields.
 */
export function terminalErrorDetail(t, data) {
  const resultCode = data?.result_code || '';
  const resultDescription = data?.result_description || '';
  const errorMsg = data?.error || '';

  // Reader-state conditions first - they are the only ones with a fix the seller can do.
  const haystack = `${resultDescription} ${errorMsg}`.toLowerCase();
  const condition = CONDITION_MARKERS.find((c) => haystack.includes(c.marker));
  if (condition) {
    const translated = t(condition.key, { defaultValue: '' });
    if (translated) return translated;
  }

  // Known result_description sentences (card-level outcomes).
  const descKey = resultDescription && t(`terminal.resultDescriptions.${resultDescription}`, { defaultValue: '' });
  if (descKey) return descKey;

  for (const knownError of KNOWN_ERRORS) {
    if (errorMsg.includes(knownError)) {
      const translated = t(`terminal.errorDescriptions.${knownError}`, { defaultValue: '' });
      if (translated) return translated;
    }
  }

  const codeKey = resultCode && t(`terminal.resultCodes.${resultCode}`, { defaultValue: '' });
  if (codeKey) return codeKey;

  return '';
}

// Reader states that clear on their own within seconds: the terminal is still finishing
// the previous transaction (showing the result, printing) when the next capture arrives.
// Worth a few automatic retries before the seller is asked to try again. NOT the
// app-not-in-front or offline conditions, which need a person at the reader.
const TRANSIENT_MARKERS = ['terminal is busy'];

export const TERMINAL_RETRY_ATTEMPTS = 3;
const TERMINAL_RETRY_DELAY_MS = 2000;

/** True for a terminal payload whose failure is expected to clear by itself shortly. */
export function isTransientTerminalCondition(data) {
  const haystack = `${data?.result_description || ''} ${data?.error || ''}`.toLowerCase();
  return TRANSIENT_MARKERS.some((marker) => haystack.includes(marker));
}

export function waitBeforeTerminalRetry() {
  return new Promise((resolve) => setTimeout(resolve, TERMINAL_RETRY_DELAY_MS));
}

/**
 * Same as terminalErrorDetail, but always returns something to show: falls back to
 * Nexi's raw description, then the raw error, then `fallbackKey`.
 */
export function terminalErrorMessage(t, data, fallbackKey) {
  const detail = terminalErrorDetail(t, data);
  if (detail) return detail;
  if (data?.result_description) return data.result_description;
  return data?.error || t(fallbackKey);
}
